import {
  Users,
  Calculator,
  UserCheck,
  Package,
  Ticket,
  Shield,
  ArrowRight,
  Layers,
  Settings2,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const Features = () => {
  const categories = [
    {
      icon: Users,
      title: "Kunden & Vertrieb",
      description: "Angebote, Aufträge und Kundenbeziehungen zentral steuern.",
      modules: ["CRM", "Angebote", "Kundenkartei"],
      color: "bg-blue-500/10 text-blue-600",
    },
    {
      icon: Calculator,
      title: "Finanzen",
      description: "Rechnungen, Mahnungen und Buchhaltung im Griff.",
      modules: ["Rechnungen", "Buchhaltung", "DATEV-Export"],
      color: "bg-emerald-500/10 text-emerald-600",
    },
    {
      icon: UserCheck,
      title: "Personal",
      description: "Mitarbeiter, Schichtpläne und Fuhrpark verwalten.",
      modules: ["Personal", "Schichtplanung", "Fuhrpark"],
      color: "bg-violet-500/10 text-violet-600",
    },
    {
      icon: Package,
      title: "Warenwirtschaft",
      description: "Lager, Einkauf und Shop-Anbindung synchron halten.",
      modules: ["Lager", "Einkauf", "Shopanbindung"],
      color: "bg-amber-500/10 text-amber-600",
    },
    {
      icon: Ticket,
      title: "Service",
      description: "Support-Tickets und Projekte effizient bearbeiten.",
      modules: ["Ticketsystem", "Projektmanagement", "Aufgaben"],
      color: "bg-rose-500/10 text-rose-600",
    },
    {
      icon: Shield,
      title: "QM & Compliance",
      description: "Dokumentenlenkung und Qualitätsmanagement strukturieren.",
      modules: ["ISO-Dokumentenlenkung", "Revision", "Freigabe"],
      color: "bg-cyan-500/10 text-cyan-600",
    },
  ];

  const steps = [
    { step: "1", title: "Plan wählen", description: "Starter, Professional oder Business – je nach Punkte-Budget." },
    { step: "2", title: "Module klicken", description: "Wählen Sie nur die Funktionen, die Sie wirklich brauchen." },
    { step: "3", title: "Sofort starten", description: "1 Monat kostenlos testen – keine Kreditkarte nötig." },
  ];

  return (
    <section id="module" className="relative overflow-hidden bg-background py-24 lg:py-32">
      <div className="absolute right-0 top-0 h-[600px] w-[600px] -translate-y-1/2 translate-x-1/2 rounded-full bg-gradient-to-br from-primary/5 to-accent/5 blur-3xl" />
      <div className="absolute bottom-0 left-0 h-[400px] w-[400px] -translate-x-1/2 translate-y-1/2 rounded-full bg-gradient-to-tr from-accent/5 to-primary/5 blur-3xl" />

      <div className="container relative">
        {/* Section Header */}
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
            <Layers className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold text-primary">Modul-Baukasten</span>
          </div>
          <h2 className="mb-6 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            Alles für Ihren
            <span className="relative mx-3 inline-block">
              <span className="relative z-10 text-primary">Workflow</span>
              <span className="absolute -bottom-1 left-0 right-0 h-3 bg-primary/10 -rotate-1" />
            </span>
          </h2>
          <p className="text-lg leading-relaxed text-muted-foreground">
            6 Kategorien, 18+ Module – kombinieren Sie frei, was zu Ihrem Unternehmen passt.
          </p>
        </div>

        {/* Category Cards */}
        <div className="mb-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((cat) => (
            <div
              key={cat.title}
              className="group relative overflow-hidden rounded-3xl border border-border bg-card p-8 transition-all duration-500 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-primary via-primary/50 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

              <div className={`mb-5 flex h-14 w-14 items-center justify-center rounded-2xl ${cat.color}`}>
                <cat.icon className="h-7 w-7" />
              </div>

              <h3 className="mb-2 font-display text-xl font-bold text-foreground">
                {cat.title}
              </h3>

              <p className="mb-4 text-sm text-muted-foreground leading-relaxed">
                {cat.description}
              </p>

              <div className="flex flex-wrap gap-1.5">
                {cat.modules.map((mod) => (
                  <span
                    key={mod}
                    className="inline-flex items-center rounded-md border border-primary/20 bg-primary/8 px-2 py-0.5 text-[11px] font-medium text-primary"
                  >
                    {mod}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Steps */}
        <div className="rounded-3xl border border-border bg-secondary/30 p-8 lg:p-10 mb-12">
          <div className="mb-8 text-center">
            <h3 className="font-display text-2xl font-bold text-foreground">So läuft der Baukasten</h3>
          </div>
          <div className="grid gap-8 lg:grid-cols-3 lg:gap-12">
            {steps.map((item, index) => (
              <div key={item.step} className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent font-display text-lg font-bold text-accent-foreground shadow-lg shadow-accent/20">
                  {item.step}
                </div>
                <div>
                  <h4 className="mb-1 font-display text-lg font-bold text-foreground">
                    {item.title}
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    {item.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Button
            size="lg"
            variant="ghost"
            className="group text-primary hover:bg-primary/5"
            asChild
          >
            <Link to="/features">
              Alle Module ansehen
              <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Features;
